// 組織設定 › 企業 SSO（plan SPEC #vms-web-sso-ui、#vms-web-sso-detail，cloud/platform#64）：只有團隊組織、只給 owner / org_admin。
//   ① IdP：選 OIDC / SAML、填欄位、顯示要給 IdP 的值（可複製）、〔測試連線〕（走一次 IdP 登入）、啟用 / 停用 / 刪除
//   ② 網域：新增、顯示 TXT 記錄、〔驗證〕
//   ③ 要求 SSO 與有效時間：開啟前第 2 級確認寫出影響（沒驗證的成員會被擋、委派改要 MFA、break-glass 名單、MFA 交由 IdP）
//   ④ break-glass 名單（只有 owner 能改，0–2 人）
//   ⑤ SCIM：端點、token 產生（明文只顯示一次）/ 撤銷、名冊與群組數（app-api /v1/orgs/{org}/scim-tokens；SDK 處理 REAUTH_REQUIRED）
// IdP / 網域的寫入要 5 分鐘內重新驗證（BFF 轉 identity 時 REAUTH_REQUIRED → 重新驗證再送）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KinioError } from '@kinio/web-sdk';
import { isOrgAdmin, orgApi, type MyOrg } from '../../lib/planeApi';
import {
  SessionError,
  orgDomainAdd,
  orgDomainDelete,
  orgDomainVerify,
  orgDomains,
  orgIdPDelete,
  orgIdPGet,
  orgIdPPut,
  ssoBegin,
  type OrgIdP,
} from '../../lib/session';
import { useConfirm } from '../../components/ConfirmDialog';
import { emailVerifyRequired, useEmailVerifyPrompt } from '../../components/EmailVerifyPrompt';
import { stamp } from '../../lib/timefmt';

const MAX_AGE_PRESETS = [900, 3600, 14400, 28800, 43200, 86400];

function codeOf(x: unknown): string {
  return x instanceof SessionError || x instanceof KinioError ? x.code : 'default';
}

function ageLabel(t: (k: string, o?: Record<string, unknown>) => string, s: number): string {
  return s < 3600 ? t('policy.minutes', { n: s / 60 }) : t('policy.hours', { n: s / 3600 });
}

export function SsoCard({ org }: { org: MyOrg }) {
  if (org.kind === 'personal' || !isOrgAdmin(org)) return null;
  return <SsoCardBody org={org} />;
}

function SsoCardBody({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  // 這個環境沒開企業 SSO（identity 對 SSO 端點一律 404，例：prod 上線前）→ 整張卡不出現，不顯示錯誤
  const q = useQuery({ queryKey: ['org-idp', org.org_id], queryFn: () => orgIdPGet(org.org_id) });
  if (q.isError && codeOf(q.error) === 'NOT_FOUND') return null;
  return (
    <section className="card" data-testid="sso-card">
      <h2 className="section-title">{t('ssoadm.title')}</h2>
      <p className="muted small">{t('ssoadm.hint')}</p>
      <IdPPart org={org} />
      <DomainsPart org={org} />
      <PolicyPart org={org} />
      <ScimPart org={org} />
    </section>
  );
}

function Err({ e }: { e: unknown }) {
  const { t } = useTranslation();
  if (!e) return null;
  const c = codeOf(e);
  const reason = e instanceof SessionError ? '' : e instanceof KinioError ? String(e.detail?.reason ?? '') : '';
  return (
    <p className="error small" role="alert" data-sso-error={c}>
      {t(`ssoadm.err_${c}`, { reason, defaultValue: t('ssoadm.err_default', { code: c }) })}
    </p>
  );
}

function IdPPart({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-idp', org.org_id], queryFn: () => orgIdPGet(org.org_id) });
  const idp = q.data?.idp ?? null;
  const [proto, setProto] = useState<'oidc' | 'saml'>('oidc');
  const [name, setName] = useState('');
  const [issuer, setIssuer] = useState('');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [entityId, setEntityId] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  const [cert, setCert] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['org-idp', org.org_id] });
  const create = useMutation({
    mutationFn: () =>
      orgIdPPut(org.org_id, {
        protocol: proto,
        display_name: name.trim(),
        config: proto === 'oidc' ? { issuer: issuer.trim(), client_id: clientId.trim() } : { idp_entity_id: entityId.trim(), sso_url: ssoUrl.trim(), certs: [{ pem: cert.trim() }] },
        ...(proto === 'oidc' && secret ? { client_secret: secret } : {}),
      }),
    onSuccess: () => {
      setSecret('');
      refresh();
    },
  });
  const status = useMutation({
    mutationFn: (s: 'active' | 'disabled') => orgIdPPut(org.org_id, { status: s, expected_version: idp?.version ?? 0 }),
    onSuccess: refresh,
  });
  const del = useMutation({ mutationFn: () => orgIdPDelete(org.org_id), onSuccess: refresh });
  const test = useMutation({
    mutationFn: async () => {
      window.location.assign(await ssoBegin(org.org_id, 'test', '/org?sso=tested'));
    },
  });
  const { ask, dialog } = useConfirm();

  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.isError) return <Err e={q.error} />;
  return (
    <div className="sso-part" data-testid="sso-idp">
      <h3 className="subsection-title">{t('ssoadm.idp')}</h3>
      {!idp && (
        <div className="grid-form">
          <label className="field">
            <span>{t('ssoadm.protocol')}</span>
            <select className="input" value={proto} onChange={(e) => setProto(e.target.value as 'oidc' | 'saml')}>
              <option value="oidc">OpenID Connect（Entra ID、Okta、Google…）</option>
              <option value="saml">SAML 2.0</option>
            </select>
          </label>
          <label className="field">
            <span>{t('ssoadm.display_name')}</span>
            <input className="input" maxLength={64} value={name} onChange={(e) => setName(e.target.value)} placeholder="Microsoft Entra ID" />
          </label>
          {proto === 'oidc' ? (
            <>
              <label className="field span-2">
                <span>Issuer</span>
                <input className="input mono" value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="https://login.microsoftonline.com/<tenant>/v2.0" />
              </label>
              <label className="field">
                <span>Client ID</span>
                <input className="input mono" value={clientId} onChange={(e) => setClientId(e.target.value)} />
              </label>
              <label className="field">
                <span>Client secret</span>
                <input className="input mono" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <label className="field span-2">
                <span>IdP Entity ID</span>
                <input className="input mono" value={entityId} onChange={(e) => setEntityId(e.target.value)} />
              </label>
              <label className="field span-2">
                <span>SSO URL</span>
                <input className="input mono" value={ssoUrl} onChange={(e) => setSsoUrl(e.target.value)} />
              </label>
              <label className="field span-2">
                <span>{t('ssoadm.cert')}</span>
                <textarea className="input mono" rows={4} value={cert} onChange={(e) => setCert(e.target.value)} placeholder="-----BEGIN CERTIFICATE-----" />
              </label>
            </>
          )}
          <Err e={create.error} />
          <div className="row-end span-2">
            <button className="btn btn-primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
              {t('ssoadm.create')}
            </button>
          </div>
        </div>
      )}
      {idp && <IdPView idp={idp} />}
      {idp && (
        <div className="row-end">
          <Err e={status.error ?? del.error ?? test.error} />
          <button className="btn" disabled={test.isPending} onClick={() => test.mutate()} data-testid="sso-test">
            {t('ssoadm.test')}
          </button>
          {idp.status === 'active' ? (
            <button className="btn" disabled={status.isPending} onClick={() => status.mutate('disabled')}>
              {t('ssoadm.disable')}
            </button>
          ) : (
            <button className="btn btn-primary" disabled={status.isPending || idp.tested_version !== idp.version} onClick={() => status.mutate('active')} data-testid="sso-activate">
              {t('ssoadm.activate')}
            </button>
          )}
          <button
            className="btn btn-danger"
            disabled={del.isPending}
            onClick={async () => {
              if (await ask({ title: t('ssoadm.delete_title'), body: t('ssoadm.delete_body'), action: t('ssoadm.delete'), danger: true })) del.mutate();
            }}
          >
            {t('ssoadm.delete')}
          </button>
        </div>
      )}
      {dialog}
    </div>
  );
}

function IdPView({ idp }: { idp: OrgIdP }) {
  const { t } = useTranslation();
  const tested = idp.tested_version === idp.version;
  return (
    <div className="grid-form" data-idp-status={idp.status}>
      <div className="span-2">
        <strong>{idp.display_name}</strong>{' '}
        <span className="small muted">
          {idp.protocol.toUpperCase()} · {t(`ssoadm.status_${idp.status}`)}
          {!tested && idp.status !== 'active' && ' · ' + t('ssoadm.untested')}
        </span>
      </div>
      {idp.pending_config && <p className="notice span-2">{t('ssoadm.pending')}</p>}
      <p className="small muted span-2">{t('ssoadm.sp_values')}</p>
      {Object.entries(idp.sp).map(([k, v]) => (
        <label key={k} className="field span-2">
          <span>{t(`ssoadm.sp_${k}`, { defaultValue: k })}</span>
          <input className="input mono" readOnly value={v} onFocus={(e) => e.target.select()} />
        </label>
      ))}
    </div>
  );
}

function DomainsPart({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-domains', org.org_id], queryFn: () => orgDomains(org.org_id) });
  const [domain, setDomain] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['org-domains', org.org_id] });
  const add = useMutation({ mutationFn: () => orgDomainAdd(org.org_id, domain.trim()), onSuccess: () => (setDomain(''), refresh()) });
  const verify = useMutation({ mutationFn: (d: string) => orgDomainVerify(org.org_id, d), onSuccess: refresh });
  const del = useMutation({ mutationFn: (d: string) => orgDomainDelete(org.org_id, d), onSuccess: refresh });
  return (
    <div className="sso-part" data-testid="sso-domains">
      <h3 className="subsection-title">{t('ssoadm.domains')}</h3>
      <p className="small muted">{t('ssoadm.domains_hint')}</p>
      {(q.data?.domains ?? []).map((d) => (
        <div key={d.domain} className="row-inline" data-domain={d.domain} data-domain-status={d.status}>
          <strong>{d.domain}</strong> <span className="small muted">{t(`ssoadm.domain_${d.status}`)}</span>
          {d.status === 'pending' && (
            <>
              <span className="small mono">
                {d.txt_name} TXT "{d.txt_value}"
              </span>
              <button className="btn btn-small" disabled={verify.isPending} onClick={() => verify.mutate(d.domain)}>
                {t('ssoadm.verify')}
              </button>
            </>
          )}
          <button className="btn btn-small" disabled={del.isPending} onClick={() => del.mutate(d.domain)}>
            {t('common.delete', { defaultValue: '刪除' })}
          </button>
        </div>
      ))}
      <div className="row-inline">
        <input className="input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.co.jp" />
        <button className="btn" disabled={!domain.trim() || add.isPending} onClick={() => add.mutate()}>
          {t('ssoadm.add_domain')}
        </button>
      </div>
      <Err e={add.error ?? verify.error ?? del.error} />
    </div>
  );
}

function PolicyPart({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-policy', org.org_id], queryFn: () => orgApi.securityPolicy(org) });
  const members = useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org), enabled: org.role === 'owner' });
  const { ask, dialog } = useConfirm();
  const cur = q.data;
  const on = !!cur?.require_sso;
  const age = cur?.sso_max_age_seconds ?? 28800;
  const bg = cur?.sso_break_glass_user_ids ?? [];
  const save = useMutation({
    mutationFn: (body: { require_sso?: boolean; sso_max_age_seconds?: number; sso_break_glass_user_ids?: string[] }) =>
      orgApi.putSecurityPolicy(org, { expected_version: cur?.version ?? 0, ...body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-policy', org.org_id] });
      void qc.invalidateQueries({ queryKey: ['me-orgs'] });
    },
  });
  const nameOf = (id: string) => {
    const m = (members.data ?? []).find((x) => x.user_id === id);
    return m?.display_name || m?.email || id;
  };
  async function toggle(next: boolean) {
    if (next && !(await ask({ title: t('ssoadm.require_title'), body: t('ssoadm.require_body', { bg: bg.length ? bg.map(nameOf).join('、') : t('ssoadm.bg_none') }), action: t('ssoadm.require_on') }))) return;
    save.mutate({ require_sso: next });
  }
  if (!cur) return null;
  const errCode = save.error ? codeOf(save.error) : '';
  return (
    <div className="sso-part" data-testid="sso-policy">
      <h3 className="subsection-title">{t('ssoadm.require')}</h3>
      <label className="check">
        <input type="checkbox" checked={on} disabled={save.isPending} onChange={(e) => void toggle(e.target.checked)} data-testid="require-sso" />
        <span>{t('ssoadm.require_label')}</span>
        <span className="small muted">{t('ssoadm.require_hint')}</span>
      </label>
      <label className="field">
        <span>{t('ssoadm.max_age')}</span>
        <select className="input" value={age} disabled={save.isPending} onChange={(e) => save.mutate({ sso_max_age_seconds: Number(e.target.value) })}>
          {[...new Set([...MAX_AGE_PRESETS, age])]
            .sort((a, b) => a - b)
            .map((s) => (
              <option key={s} value={s}>
                {ageLabel(t, s)}
              </option>
            ))}
        </select>
      </label>
      <Err e={save.error} />
      {errCode === 'SSO_PROOF_REQUIRED' && <Link to={'/sso/step-up?org=' + encodeURIComponent(org.org_id) + '&return=/org'}>{t('scope.go_sso')}</Link>}
      <h3 className="subsection-title">{t('ssoadm.break_glass')}</h3>
      <p className="small muted">{t('ssoadm.break_glass_hint')}</p>
      {org.role === 'owner' ? (
        <div data-testid="sso-break-glass">
          {(members.data ?? [])
            .filter((m) => m.status === 'active')
            .map((m) => (
              <label key={m.user_id} className="check">
                <input
                  type="checkbox"
                  checked={bg.includes(m.user_id)}
                  disabled={save.isPending || (!bg.includes(m.user_id) && bg.length >= 2)}
                  onChange={(e) => save.mutate({ sso_break_glass_user_ids: e.target.checked ? [...bg, m.user_id] : bg.filter((x) => x !== m.user_id) })}
                />
                <span>{m.display_name || m.email || m.user_id}</span>
              </label>
            ))}
        </div>
      ) : (
        <p className="small">{bg.length ? bg.map(nameOf).join('、') : t('ssoadm.bg_none')}</p>
      )}
      {dialog}
    </div>
  );
}

function ScimPart({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-scim', org.org_id], queryFn: () => orgApi.scim(org) });
  const [shown, setShown] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['org-scim', org.org_id] });
  // 403 EMAIL_NOT_VERIFIED →「先驗證你的 email」（platform#176）
  const verify = useEmailVerifyPrompt(() => create.mutate());
  const create = useMutation({ mutationFn: () => orgApi.createScimToken(org), onSuccess: (r) => (setShown(r.token), refresh()), onError: (e) => verify.ask(e) });
  const revoke = useMutation({ mutationFn: (id: string) => orgApi.revokeScimToken(org, id), onSuccess: refresh });
  const { ask, dialog } = useConfirm();
  const d = q.data;
  // 回應形狀不齊（舊後端、代理改寫）時照有的畫：讀不到 counts / scim_tokens 不能讓整個 App 崩成空白頁
  const counts = d?.counts;
  const toks = d?.scim_tokens ?? [];
  return (
    <div className="sso-part" data-testid="sso-scim">
      {verify.prompt}
      <h3 className="subsection-title">{t('ssoadm.scim')}</h3>
      <p className="small muted">{t('ssoadm.scim_hint')}</p>
      {q.isError && <Err e={q.error} />}
      {d && (
        <>
          <label className="field span-2">
            <span>{t('ssoadm.scim_endpoint')}</span>
            <input className="input mono" readOnly value={d.endpoint} onFocus={(e) => e.target.select()} />
          </label>
          {counts && (
            <p className="small">
              {t('ssoadm.scim_counts', counts)} · {counts.last_sync_at ? t('ssoadm.scim_last_sync', { at: stamp(counts.last_sync_at) }) : t('ssoadm.scim_never')}
            </p>
          )}
          {toks.map((k) => (
            <div key={k.token_id} className="row-inline" data-scim-token={k.token_id}>
              <span className="small">
                {t('ssoadm.scim_token_row', { at: stamp(k.created_at) })}
                {k.last_used_at && ' · ' + t('ssoadm.scim_token_used', { at: stamp(k.last_used_at) })}
              </span>
              <button
                className="btn btn-small btn-danger"
                disabled={revoke.isPending}
                onClick={async () => {
                  if (await ask({ title: t('ssoadm.scim_revoke_title'), body: t('ssoadm.scim_revoke_body'), action: t('ssoadm.scim_revoke'), danger: true })) revoke.mutate(k.token_id);
                }}
              >
                {t('ssoadm.scim_revoke')}
              </button>
            </div>
          ))}
          {shown && (
            <div className="notice" data-testid="scim-token-shown">
              <p className="small">{t('ssoadm.scim_new_shown')}</p>
              <input className="input mono" readOnly value={shown} onFocus={(e) => e.target.select()} />
            </div>
          )}
          <div className="row-end">
            <Err e={(emailVerifyRequired(create.error) ? null : create.error) ?? revoke.error} />
            <button className="btn" disabled={create.isPending || toks.length >= 2} onClick={() => create.mutate()} data-testid="scim-new">
              {t('ssoadm.scim_new')}
            </button>
          </div>
        </>
      )}
      {dialog}
    </div>
  );
}
